import React from 'react';
import PropTypes from 'prop-types';
import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';
import Image from 'src/components/Image';

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const EnVersion = ({ id, availableLanguages }) => {
  return (
    <>
      <h4
        id={id || 'privacy-statement'}
        data-magellan-target={id || 'privacy-statement'}
      >
        Privacy Statement
      </h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Effective Date: February 22, 2024</p>
      <p>
        Thank you for using Plaid! We hope you will take some time to read this
        Privacy Statement carefully.
      </p>
      <p>
        This Privacy Statement explains the ways Plaid Inc. and its
        subsidiaries, including Plaid Financial Ltd., Plaid Consumer Reporting
        Agency, Inc., and Plaid, B.V., (collectively, “Plaid”, “we”, “our”, and
        “us”) collect, use, and share information about you (ie. the developer,
        customer, website visitor) when you visit or use Plaid’s websites, or
        incorporate our products and services into your software application ,
        or otherwise interact with us (for example, when you make a customer
        service inquiry or attend a Plaid event or webinar). This Privacy
        Statement does not apply to information we collect about end users when
        they connect their financial accounts to an app through Plaid, or to the
        data used to create a Plaid CRA consumer report. If you’re looking for
        information as to how we handle data collected under those
        circumstances, please see our{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>{' '}
        or{' '}
        <Link href='/plaid-check-consumer-report/privacy-policy/'>
          <a>Plaid Consumer Reporting Agency Privacy Policy</a>
        </Link>
        .
      </p>
      <p>
        <em>Jump to section:</em>
      </p>
      <p>
        <Link href='#our-data-practices-privacy'>
          <a data-smooth-scroll data-offset='80'>
            Our Data Practices
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#information-we-collect-and-categories-of-sources-privacy'>
              <a>Information We Collect and Categories of Sources</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#how-we-use-your-information-privacy'>
              <a>How We Use Your Information</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#our-lawful-bases-for-processing-privacy'>
              <a>Our Lawful Bases for Processing in the EEA and UK</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#how-we-share-your-information-privacy'>
              <a>How We Share Your Information</a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#other-details-privacy'>
          <a data-smooth-scroll data-offset='80'>
            Other Details
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#chatbots-advertising-and-analytics-services'>
              <a>
                Chatbots, Advertising, Analytics and other Services Provided by
                Third-parties
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#international-data-transfers'>
              <a>International Data Transfers</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#data-retention'>
              <a>Data Retention</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#your-choices-privacy'>
              <a>Your Choices</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#your-data-protection-rights-privacy'>
              <a>Your Data Protection Rights</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#changes-to-this-privacy-statement'>
              <a>Changes to this Statement</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#contacting-plaid-privacy'>
              <a>Contacting Plaid</a>
            </Link>
          </p>
        </li>
      </ul>
      <br />
      <br />

      <h4 id='our-data-practices-privacy'>Our Data Practices</h4>
      <h5 id='information-we-collect-and-categories-of-sources-privacy'>
        Information We Collect and Categories of Sources
      </h5>
      <p>
        <strong>Information you provide to us</strong>. We collect the
        information you provide directly to Plaid. For example, you provide
        personal information when you create a developer account, fill out our
        “contact us” form, sign up for our emails, request customer support,
        enroll in billing, or otherwise communicate with us. The types of
        information we collect include:
      </p>
      <ul>
        <li>
          <p>
            identifiers (like full name, email address, postal address, phone
            number, and date of birth),
          </p>
        </li>
        <li>
          <p>
            sensitive information (like a driver’s license number, and taxpayer
            identification number, financial account information, and Social
            Security Numbers as necessary to provide services),
          </p>
        </li>
        <li>
          <p>
            professional information (like as a company name and title), and
          </p>
        </li>
        <li>
          <p>
            any other personal information you choose to provide (including, for
            example, in developer support messages).
          </p>
        </li>
      </ul>
      <p>
        We may also collect certain documentation that contains your photograph
        or we may require you to verify your identity by providing a photograph
        or video of yourself. Data collected in this context may be considered
        biometric data in some states or countries, as noted below. See the
        special notice for Illinois and Texas residents, below.
      </p>
      <p>
        <u>Special Biometric Data Notice for Illinois and Texas Residents</u>
      </p>
      <p>
        For residents of Illinois or Texas, if we require you to provide us with
        any document that contains your photograph or if you need to verify your
        identity by providing a photograph or video of yourself, the data
        derived from your face that we and our service providers collect and
        process on our behalf may be considered biometric data in some places.
        Your data will be stored no longer than three years, unless required by
        law.
      </p>
      <p>
        <strong>Information we receive when you test our technology</strong>.
        Sometimes people who build applications using our services will use
        their own login information for their bank account or other financial
        account to test and evaluate how our technology will appear and operate
        in their application. If you do this, you should read our{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>{' '}
        for details on how we collect and handle that information.
      </p>
      <p>
        <strong>Information we collect when you use our services</strong>. When
        you use our services, we collect the following information about you:
      </p>
      <ul>
        <li>
          <p>
            <strong>Log Information</strong>: We collect log files when you use
            our services. This includes identifiers and electronic network
            activity information, like the type of browser you use, when you
            access our services, the pages viewed, and your IP address.
          </p>
        </li>
        <li>
          <p>
            <strong>Approximate Location</strong>: We determine the approximate
            location of the device you use to access our services from your IP
            address or timezone setting on your device.
          </p>
        </li>
        <li>
          <p>
            <strong>
              Information Collected by Cookies and Other Tracking Technologies
            </strong>
            : We use various technologies like cookies and web beacons in our
            services to collect personal information. Cookies are small data
            files stored by your web browser, on your hard drive, or in device
            memory that help us improve our services and your experience,
            determine usage of our services, and monitor for and detect
            potential harmful conduct. Web beacons are electronic images that
            are used in our services or emails and help deliver cookies. Web
            beacons count how many times a webpage is visited, and help us
            understand how our Service is being used and improve it.
          </p>
          <ul>
            <li>
              <p>
                Please note that we may use third-party cookies for our
                advertising purposes, as permitted by law and as further
                described in our{' '}
                <Link href='#cookie-policy'>
                  <a>Cookie Policy</a>
                </Link>
                . To change your cookie settings and preferences, including
                those for targeting and advertising cookies, click the Cookie
                Preferences link in the footer of the page.
              </p>
            </li>
          </ul>
        </li>
      </ul>
      <p>
        <strong>Information we collect from other sources</strong>. We also
        collect information about you from other sources. For example, we may
        collect information from other members of your company and from vendors
        that help us identify new business customers, including your name, email
        address, and social media profile URL.
      </p>

      <h5 id='how-we-use-your-information-privacy'>
        How We Use Your Information
      </h5>
      <p>
        We use the information we collect for the following business and
        commercial purposes:
      </p>
      <ul>
        <li>
          <p>To operate, improve, and develop our services;</p>
        </li>
        <li>
          <p>To verify your identity;</p>
        </li>
        <li>
          <p>To bill developers for our services and to pay them;</p>
        </li>
        <li>
          <p>To comply with law, like for tax reporting purposes;</p>
        </li>
        <li>
          <p>
            To send you thing like technical notices, updates, security alerts,
            and messages about your account;
          </p>
        </li>
        <li>
          <p>
            To respond to your comments, questions, and customer service
            requests;
          </p>
        </li>
        <li>
          <p>To help personalize the services experience for you;</p>
        </li>
        <li>
          <p>
            To communicate with you about products, services, offers, and Plaid
            events we offer or sponsor;
          </p>
        </li>
        <li>
          <p>
            To monitor and analyze trends, use, and activities in connection
            with our services;
          </p>
        </li>
        <li>
          <p>
            To detect and prevent fraud, malicious activity, and other illegal
            activities;
          </p>
        </li>
        <li>
          <p>
            To protect the rights, privacy, safety, or property of Plaid and
            others; and
          </p>
        </li>
        <li>
          <p>
            For other purposes that we notify you about before or at the time we
            collect the information.
          </p>
        </li>
      </ul>

      <h5 id='our-lawful-bases-for-processing-privacy'>
        Our Lawful Bases for Processing in the EEA and UK
      </h5>
      <p>
        Plaid only processes your personal information when we have a valid
        legal basis to do so. Our legal basis for processing the information we
        collect will depend on what information we collected and the context for
        processing it. Generally, we only collect and process information where:
      </p>
      <ul>
        <li>
          <p>
            we need to fulfill our responsibilities and obligations in any
            contract or agreement with you;
          </p>
        </li>
        <li>
          <p>to comply with our legal obligations under applicable law;</p>
        </li>
        <li>
          <p>
            the processing is necessary for our legitimate interests and not
            overridden by your data protection interests or fundamental rights
            and freedoms (for example, to provide or update our services); and
          </p>
        </li>
        <li>
          <p>you have given your consent to do so.</p>
        </li>
      </ul>

      <h5 id='how-we-share-your-information-privacy'>
        How We Share Your Information
      </h5>
      <p>We share information about you as follows:</p>
      <ul>
        <li>
          <p>
            With other companies or individuals as service providers,
            contractors, or business partners to provide services for us and
            help support our business. We also share information with collection
            agencies in the event of delinquent payments from our developers.
          </p>
        </li>
        <li>
          <p>
            If we believe in good faith that disclosure is appropriate to comply
            with applicable law, regulation, or legal process (such as a court
            order or subpoena). This includes disclosure in connection with
            requests from law enforcement or other governmental authorities;
          </p>
        </li>
        <li>
          <p>
            If we believe your actions are inconsistent with our agreements or
            policies, or to protect the rights, privacy, safety, or property of
            Plaid or others;
          </p>
        </li>
        <li>
          <p>
            In the event of a change in ownership or control of all or part of
            our business (such as a merger, acquisition, reorganization, or
            bankruptcy);
          </p>
        </li>
        <li>
          <p>
            Between Plaid and our current and future parents, affiliates,
            subsidiaries and other companies under common control or ownership;
            and
          </p>
        </li>
        <li>
          <p>With your consent or at your direction.</p>
        </li>
      </ul>
      <p>
        We also collect, use, and share information that has been aggregated and
        de-identified in a way so it cannot be used to identify you, for any
        purpose permitted under applicable law.
      </p>
      <p>
        We may share Personal Data with third parties or allow them to collect
        Personal Data from our sites or services, as described above.
      </p>
      <p>
        This table explains how we have collected and shared personal
        information in the last 12 months.
      </p>
      <br />

      <p>CATEGORIES OF PERSONAL INFORMATION</p>
      <Image
        src='https://images.ctfassets.net/ss5kfr270og3/2oAMRgMy4bo59fktLW0Ss5/709b73d35c115c4243e834f4d9e281fc/personal-information-table-en_2x.png?fm=webp&q=70'
        alt='Personal Information table'
        isLazy={false}
      />

      <h4 id='other-details-privacy'>Other Details</h4>
      <h5 id='chatbots-advertising-and-analytics-services'>
        Chatbots, Advertising, Analytics and other Services Provided by
        Third-parties
      </h5>
      <p>
        We allow third parties, including Google Analytics, to provide analytics
        services and advertisements on our websites. These third parties use
        cookies, web beacons, and other technologies to collect information
        about you. Plaid uses chatbots that are powered by third-party service
        providers, please be aware that if you provide personal information to
        our chatbot, your information may be shared with the third-party service
        provider for the purposes of powering the chatbot.
      </p>
      <p>
        You can find more information about how we use cookies and your related
        choices in our{' '}
        <Link href='#cookie-policy'>
          <a>Cookie Policy</a>
        </Link>
        .
      </p>

      <h5 id='international-data-transfers'>International Data Transfers</h5>
      <p>
        We may transfer the information we collect about you to countries other
        than the one in which you live for processing and storage. If we
        transfer information to a country or territory that has not been deemed
        to have equivalent privacy and data protection laws to your country we
        will transfer such data consistent with applicable data protection laws.
        In particular we may use EU Commission-approved or UK Secretary of
        State-approved (as applicable) standard contractual clauses, if
        necessary in combination with additional safeguards. You can ask for a
        copy of these standard contractual clauses by contacting us as set out
        below.
      </p>

      <h5 id='data-rentention'>Data Retention</h5>
      <p>
        We retain your personal data for no longer than necessary to achieve the
        purposes for which it was collected, unless a longer retention period is
        required under applicable law.
      </p>

      <h5 id='your-choices-privacy'>Your Choices</h5>
      <ul>
        <li>
          <p>
            <strong>Developer Account Information</strong>. You may update
            information you provide to us as part of your online developer
            account by logging into your account or by contacting us.
          </p>
        </li>
        <li>
          <p>
            <strong>Cookies</strong>. You can find more information about how we
            use cookies and your related choices in our{' '}
            <Link href='#cookie-policy'>
              <a>Cookie Policy</a>
            </Link>
            .
          </p>
        </li>
        <li>
          <p>
            <strong>Marketing Communications</strong>. You may opt in and opt
            out of receiving marketing emails from Plaid by following the
            instructions in those emails. If you opt out, we may still send you
            other types of emails, such as support, service, and other emails
            about your account or our ongoing business relationship.
          </p>
        </li>
      </ul>

      <h5 id='your-data-protection-rights-privacy'>
        Your Data Protection Rights
      </h5>
      <p>
        You may have rights related to your personal information, including the
        right to know how we collect, use, or share your information.
        Additionally, you may have the right to request access to your personal
        information, as well as to seek to update, delete, or correct that
        information. We will honor your request to exercise these rights
        regardless of your location. You will not be discriminated against for
        exercising these rights.
      </p>
      <p>
        In certain regions, including the EEA and UK, you may have additional
        rights available to you. These rights include the right to: object to,
        or request that we restrict, the processing of your personal data;
        revoke consent at any time where our processing is based on your
        consent; and lodge a complaint with your local supervisory authority (if
        you’re in the EEA, please see{' '}
        <Link href='https://edpb.europa.eu/about-edpb/board/members_en'>
          <a>https://edpb.europa.eu/about-edpb/board/members_en</a>
        </Link>
        , and if you’re in the UK, please see{' '}
        <Link href='https://ico.org.uk/'>
          <a>www.ico.org.uk</a>
        </Link>{' '}
        for contact details).
      </p>
      <p>
        For both Plaid Financial Ltd. and Plaid, B.V. we have appointed a data
        protection officer (DPO) who is also responsible for overseeing
        questions in relation to this Policy and can be contacted via email at{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        If you are a California resident, subject to limitations and exceptions
        provided by law, some additional rights may be available to you under
        the California Consumer Protection Act (“CCPA”) and as amended by the
        California Privacy Rights Act. You may also request access to the
        categories and specific pieces of personal information collected about
        you in the last twelve months (including personal information disclosed
        for business purposes); know if any of your personal information has
        been "sold" or "shared" and to whom; opt-out of any “sales” or “sharing”
        of your personal information if a business is engaged in “selling” or
        “sharing” your information; and request a business correct inaccurate
        personal information. Please note that while we do not sell your
        personal information we may share it with third parties for advertising
        purposes using “cookies,” as described in our cookie policy.
      </p>
      <p>
        As described above in “Your Choices,” if you are one of our developers,
        you can conveniently access your online account information by logging
        into your account or by contacting us. To otherwise exercise your data
        protection rights, where applicable, please contact us as described in
        the “Contacting Plaid” section below. You may be required to provide
        additional information to confirm your identity before we can respond to
        your request.
      </p>
      <p>
        If we receive your request from an authorized agent, we may ask for
        evidence that you have provided the agent with valid written authority
        to submit requests on your behalf. If you are an authorized agent
        seeking to make a request, please contact us as described in the
        “Contacting Plaid” section below.
      </p>

      <h5 id='changes-to-this-privacy-statement'>Changes to this Statement</h5>
      <p>
        We may update or change this Privacy Statement periodically. If we make
        any updates or changes, we will change the effective date at the top of
        this Statement. We may also provide notice of any changes through other
        means, such as placing a notice on our homepage at{' '}
        <Link href='https://plaid.com'>
          <a>https://plaid.com</a>
        </Link>{' '}
        or sending you an email. We encourage you to review the Privacy
        Statement whenever you access the services or otherwise interact with us
        to stay informed about our data practices and the choices available to
        you.
      </p>

      <h5 id='contacting-plaid-privacy'>Contacting Plaid</h5>
      <p>
        If you have any questions about this Privacy Statement, or our privacy
        practices generally, please contact us at{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        or by mail at:
      </p>
      <p>
        <em>If you reside outside the EEA or UK:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Inc. <br />
        Attn: Legal <br />
        PO Box 7775 #35278 <br />
        San Francisco, California 94120-7775, U.S.A.
      </div>
      <br />
      <p>
        For both Plaid Financial Ltd. and Plaid, B.V. we have appointed a data
        protection officer (DPO) who is also responsible for overseeing
        questions in relation to this Policy and can be contacted via email at{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        <br />
        <em>If you reside in the UK:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Financial Ltd. <br />
        Attn: Legal <br />
        New Penderel House, 4th Floor <br />
        283-288 High Holborn <br />
        London, United Kingdom, WC1V 7HP
      </div>
      <p>
        <br />
        <em>If you reside in the EEA:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid, B.V. <br />
        Attn: Legal <br />
        Muiderstraat 1 <br />
        1011PZ Amsterdam <br />
        The Netherlands
      </div>

      <hr className='section-divider' />
    </>
  );
};

EnVersion.propTypes = propTypes;
export default EnVersion;
